import { useState, React, useEffect } from "react";
import Copyright from "../LoginComponents/Copyright";
import {
  Box,
  Grid,
  Typography,
  Container,
  Button,
  TextField, Link,
  Tooltip,
} from "@mui/material";
import { successNote, toastErrorNotify } from "../../helpers/toastNotify";
import Loading from "../../layout/Loading";
import { ValidateOTP, SendPhoneValidationOTP, ResendExpiredInvitation  } from "../../api/endpoints";
import apiClient from "../../api/apiClient";
import { useNavigate, } from "react-router-dom";
import * as Yup from "yup";

// Exact text of the backend's PhoneOTPUsageRight <= 0 message (OrgAdminUserAuthManager
// .SendPhoneValidationOTP, hit here via "Send igjen") - matched so it never reaches the Norwegian
// UI as raw English, and so this dead end gets the same self-service recovery
// PhoneVerification.js's expired-invitation screen offers, instead of leaving the admin stuck
// needing someone else to resend their invite by hand.
const OTP_LIMIT_REACHED_MESSAGE =
  "Please contact MakingVeiw AS. Your registration attempt has surpassed the general count.";

// mm:ss display for the resend cooldown countdown.
const formatCooldown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

// ASP.NET's automatic model validation runs before our own FluentValidation/business logic -
// if a required field (e.g. `phone`) is missing from the request body entirely (which is what
// happens when userData.phone is undefined, since JSON.stringify drops undefined keys), it
// short-circuits with a generic ValidationProblemDetails body: no `message`, just a `title` of
// "One or more validation errors occurred." and an `errors` dictionary of field -> messages. That
// raw English title used to reach the user verbatim via the `err.response.data.title` fallback
// below - this translates it into something actionable instead.
const getFriendlyValidationMessage = (err) => {
  const errors = err.response?.data?.errors;
  if (!errors) return null;
  const missingPhone = Object.keys(errors).some((key) => key.toLowerCase().includes("phone"));
  return missingPhone
    ? "Mobilnummer mangler. Klikk på 'Endre mobilnummer' og skriv det inn på nytt."
    : "Noe gikk galt med forespørselen. Prøv igjen, eller skriv inn mobilnummeret på nytt.";
};

export default function OTPValidation() {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState({});
  const yupErrors = {};
  const storedUserData = localStorage.getItem('userData');
const userData = storedUserData ? JSON.parse(storedUserData) : null;
// "Send igjen" needs a phone number to resend an OTP to - true unless the previous page (or a
// direct "Har skrevet inn mobilnummeret før" arrival) never captured one.
const hasPhone = Boolean(userData?.phone);
const [otpRequestCount, setOtpRequestCount] = useState(0);
const [limitReached, setLimitReached] = useState(false);
const [resendingInvitation, setResendingInvitation] = useState(false);
const [invitationResent, setInvitationResent] = useState(false);
// True when the most recent resend click was blocked by the backend's cooldown - distinct from
// invitationResent so the UI never claims a fresh email went out when it didn't.
const [resendBlocked, setResendBlocked] = useState(false);
// Seconds until another resend will actually send an email (backend-enforced cooldown, see
// OrganizationManager.ResendInvitationForAdminAsync).
const [cooldownRemaining, setCooldownRemaining] = useState(0);
const authBackgroundImage = 'https://res.cloudinary.com/dge9ers9x/image/upload/v1693381778/Making%20View%20Assets/auth_background_texcon.jpg'
const logoRed =  'https://res.cloudinary.com/dge9ers9x/image/upload/v1693381822/Making%20View%20Assets/MV_logo_red_black_ctm6tn.png'


  const validationSchema = Yup.object().shape({
    validationOTP: Yup.string().length(4, "'Kode' må være 4 tegn lang.").required("Verifiseringkode er påkrevd"),
  });


  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);

    if (otpRequestCount >= 2) {  // if there are requests more then 3 times
      toastErrorNotify("Du kan ikke be om OTP mer enn 3 ganger");  // show error
      setLoading(false); 
      return; 
  }


    const data = new FormData(event.currentTarget);
    const initialValues = {
      validationOTP: data.get("otp"),
    };

    try {
      await validationSchema.validate(initialValues, { abortEarly: false });
      //! If validation succeeds, proceed with form submission
     const response=  await apiClient.post(ValidateOTP, {
      validationOTP: data.get("otp"),
      registrationKey: userData.registrationKey,
      email: userData.email,
      phone: userData.phone,
    });
    setOtpRequestCount(otpRequestCount + 1);
      if (userData.adminType === "super"){
      navigate("/registerroot");
      }else if (userData.adminType === "lower"){
      navigate("/register");
      }
      // console.log(JSON.stringify(response?.data));
    } catch (err) {
      // console.log('err', err)
      //! If validation fails, display error messages
      const friendlyValidationMessage = getFriendlyValidationMessage(err);
      if (err?.inner) {
        err.inner.forEach((e) => {
          yupErrors[e.path] = e.message;
        });
        setValidationErrors(yupErrors);
      } else if (friendlyValidationMessage) {
        toastErrorNotify(friendlyValidationMessage);
      }else if(err.response.data.message){
        toastErrorNotify(err.response.data.message);
      }else if(err.response.data.title){
        toastErrorNotify(err.response.data.title);
      } else {
        toastErrorNotify("Mislyktes");
      }
    } finally {
      setLoading(false);
    }
  };

  //! Send OTP Again

  const fetchOTPAgain = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await apiClient.post(SendPhoneValidationOTP, {
        validationKey:userData.validationKey,
        registrationKey: userData.registrationKey,
        email: userData.email,
        phone: userData.phone,
      });
      successNote("Verifiseringkode sent");
    } catch (err) {
      //! If validation fails, display error messages
      const friendlyValidationMessage = getFriendlyValidationMessage(err);
      if (err?.inner) {
        err.inner.forEach((e) => {
          yupErrors[e.path] = e.message;
        });
        setValidationErrors(yupErrors);
      } else if (err.response?.data?.message === OTP_LIMIT_REACHED_MESSAGE) {
        setLimitReached(true);
      } else if (friendlyValidationMessage) {
        toastErrorNotify(friendlyValidationMessage);
      }else if(err.response.data.message){
        toastErrorNotify(err.response.data.message);
      }else if(err.response.data.title){
        toastErrorNotify(err.response.data.title);
      } else {
        toastErrorNotify("Mislyktes");
      }
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const isCoolingDown = cooldownRemaining > 0;
  useEffect(() => {
    if (!isCoolingDown) return undefined;
    const intervalId = setInterval(() => {
      setCooldownRemaining((prev) => Math.max(prev - 1, 0));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [isCoolingDown]);

  const handleResendInvitation = async () => {
    setResendingInvitation(true);
    try {
      const response = await apiClient.post(ResendExpiredInvitation, {
        orgId: userData.orgId,
        userId: userData.userId,
        email: userData.email,
      });
      const result = response?.data?.data || {};
      setCooldownRemaining(result.cooldownSecondsRemaining || 0);
      if (result.sent) {
        setInvitationResent(true);
        setResendBlocked(false);
        successNote("Ny invitasjon sendt");
      } else {
        // Cooldown blocked this click - no email went out, so don't claim one did. The backend
        // also retires the link currently in use in this case, so any earlier "sent" confirmation
        // is stale too - the countdown is the only way forward now.
        setInvitationResent(false);
        setResendBlocked(true);
        toastErrorNotify("Du ba nylig om en ny lenke. Vent til nedtellingen er ferdig før du kan be om en ny.");
      }
    } catch (err) {
      if (!err.response) {
        toastErrorNotify("Ingen svar fra serveren");
      } else if (err.response.data?.message) {
        toastErrorNotify(err.response.data.message);
      } else {
        toastErrorNotify("Mislyktes");
      }
    } finally {
      setResendingInvitation(false);
    }
  };


  return (
    <Grid
      container
      component="main"
      sx={{
        backgroundImage: `url(${authBackgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "130vh",
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            backgroundColor: "rgba(255,255,255, 0.9)",
            p: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            borderRadius: 5,
          }}
        >
          <img src={logoRed} alt="Logo" className="logoLogin" />
          {limitReached ? (
            <>
              <Typography component="h1" variant="h5" sx={{ mt: 2 }}>
                Du har brukt opp forsøkene dine
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5, textAlign: "center" }}>
                Du har bedt om for mange bekreftelseskoder på denne invitasjonen. Du er fortsatt
                registrert som administrator hos organisasjonen, så du kan be om en ny invitasjon
                under - den nullstiller antall forsøk.
              </Typography>
              {invitationResent && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "success.main" }}>
                  Ny invitasjon sendt til {userData?.email}. Sjekk innboksen din og åpne lenken i
                  den nye e-posten for å fortsette registreringen.
                </Typography>
              )}
              {resendBlocked && (
                <Typography sx={{ mt: 2, fontSize: 14, textAlign: "center", color: "warning.main" }}>
                  Du ba nylig om en ny lenke, så denne lenken er ikke lenger aktiv. Vent til
                  nedtellingen under er ferdig for å be om en ny.
                </Typography>
              )}
              <Button
                className="PrimaryButton"
                fullWidth
                variant="contained"
                disabled={resendingInvitation || cooldownRemaining > 0}
                onClick={handleResendInvitation}
                sx={{ mt: 3, mb: cooldownRemaining > 0 ? 1 : 2 }}
              >
                {resendingInvitation ? <Loading color={"#fff"} size={25} /> : "Send meg en ny invitasjon"}
              </Button>
              {cooldownRemaining > 0 && (
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1 }}>
                  Du kan be om en ny lenke om {formatCooldown(cooldownRemaining)}
                </Typography>
              )}
              <Copyright sx={{ mt: 1 }} />
            </>
          ) : (
            <>
              <Typography component="h1" variant="h5">
              Bekreft verifiseringkode
              </Typography>
              <Typography sx={{ mt: 2, fontSize: 15.5 }}>
                Hvis du ikke mottar OTP, kan du be om å sende det igjen ved å klikke på 'Send igjen'. <br />
                <br />Det er mulig å motta en ny OTP opptil 3 ganger.
              </Typography>
              <Box
                component="form"
                noValidate
                onSubmit={handleSubmit}
                sx={{ mt: 3 }}
              >
                    <TextField
                    sx={{mb:2}}
                      required
                      fullWidth
                      id="otp"
                   label="Verifiseringkode"
                   name="otp"
                     error={Boolean(validationErrors.validationOTP)}
                      helperText={validationErrors.validationOTP}
                    />
                     <Box sx={{display:"flex", justifyContent:"space-between"}}>
       <Tooltip
         title={hasPhone ? "" : "Du må skrive inn mobilnummeret ditt på forrige side før du kan be om en ny kode"}
         disableHoverListener={hasPhone}
       >
         <span>
           <Link
             onClick={hasPhone ? fetchOTPAgain : undefined}
             sx={{
               cursor: hasPhone ? 'pointer' : 'not-allowed',
               color: hasPhone ? undefined : 'text.disabled',
               pointerEvents: hasPhone ? 'auto' : 'none',
               mr: 6,
             }}
           >Send igjen</Link>
         </span>
       </Tooltip>
       <Link onClick={()=>navigate('/phoneverification', { state: {userData} })} sx={{cursor: 'pointer', ml: 5}}>Endre mobilnummer</Link>
     </Box>
                <Button
                  className="PrimaryButton"
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={loading}
                  sx={{ mt: 3, mb: 2 }}
                >
                  {loading ? <Loading color={"#fff"} size={25} /> : "Verify"}
                </Button>
                <Copyright sx={{ mt: 1 }} />
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Grid>
  );
}
